import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';

import { SkeletonLoaderService } from 'app/shared/services/skeleton-loader.service';

import { SkeletonLoaderComponent } from 'app/shared/components/skeleton-loader/skeleton-loader.component';

import { ImageProtectionDirective } from 'app/shared/directives/image-protection.directive';

import { IMediaLoadingState, IImageEvent } from 'app/shared/interfaces/media.interfaces';

import { EImageUsage } from 'app/shared/enums/image-usage.enums';

@Component({
  selector: 'stxt-image',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, SkeletonLoaderComponent, ImageProtectionDirective],
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss'
})
export class ImageComponent implements OnInit, OnDestroy {
  @Input() src: string = '';
  @Input() alt: string = 'Image';
  @Input() mediaUsage: EImageUsage = EImageUsage.Gallery;
  @Input() tabindex: number = -1;
  @Input() fill: boolean = true;
  @Input() priority: boolean = true;
  @Input() staggerDelay: number = 0;
  @Input() backgroundImageSrc: string = '';
  @Input() showBackgroundImage: boolean = false;
  @Input() imageLocked: boolean = false;

  @Output() imageEvent: EventEmitter<IImageEvent> = new EventEmitter<IImageEvent>();

  public loadingState: IMediaLoadingState = {
    isLoaded: false,
    isRendered: false,
    isError: false
  };

  private skeletonTimeout?: ReturnType<typeof globalThis.setTimeout>;
  private showSkeleton: boolean = false;

  constructor(private readonly skeletonLoaderService: SkeletonLoaderService) {}

  ngOnInit(): void {
    this.initializeLoadingState();
  }

  private initializeLoadingState(): void {
    this.loadingState = {
      isLoaded: false,
      isRendered: false,
      isError: false
    };
    this.showSkeleton = false;

    if (this.staggerDelay > 0) {
      setTimeout(() => {
        this.loadingState.isLoaded = true;
        this.startSkeletonTimer();
        this.emitStateChange();
      }, this.staggerDelay);
    } else {
      this.loadingState.isLoaded = true;
      this.startSkeletonTimer();
      this.emitStateChange();
    }
  }

  private startSkeletonTimer(): void {
    const skeletonDelay = this.mediaUsage === EImageUsage.MessageMedia ? 0 : 100;

    // setTimeout is used here to delay showing the skeleton loader
    this.skeletonTimeout = globalThis.setTimeout(() => {
      if (this.loadingState.isRendered || this.loadingState.isError) return;
      this.showSkeleton = true;
    }, skeletonDelay);
  }

  onImageStateChange(event: Event): void {
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
      this.skeletonTimeout = undefined;
    }

    this.skeletonLoaderService.handleImageStateChange(event, this.loadingState).then(() => {
      this.emitStateChange();
      if (event.type === 'load') {
        this.imageEvent.emit({ type: 'load', event });
      } else if (event.type === 'error') {
        this.imageEvent.emit({ type: 'error', event });
      }
    });
  }

  shouldShowSkeleton(): boolean {
    return (
      this.loadingState.isLoaded &&
      !this.loadingState.isRendered &&
      !this.loadingState.isError &&
      this.showSkeleton
    );
  }

  shouldShowImage(): boolean {
    return this.src?.length > 0 && this.loadingState.isLoaded;
  }

  getUsageClass(): string {
    const usageClassMap: Partial<{ [key in EImageUsage]: string }> = {
      [EImageUsage.Profile]: 'profile',
      [EImageUsage.Gallery]: 'gallery',
      [EImageUsage.ImagePresets]: 'image-presets',
      [EImageUsage.Timeline]: 'timeline',
      [EImageUsage.MessageMedia]: 'message-media'
    };

    return usageClassMap[this.mediaUsage] || '';
  }

  getBackgroundImageUrl(): string {
    return this.backgroundImageSrc || this.src;
  }

  private emitStateChange(): void {
    this.imageEvent.emit({
      type: 'stateChange',
      state: { ...this.loadingState }
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.skeletonTimeout);
  }
}
